import React, { useMemo, useState } from 'react';
import ModernSelect, { ModernSelectOption } from 'src/components/ModernSelect';
import Toast from '../../components/Toast';
import Button from '../../components/Button';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { STATUSES, STATUS_MESSAGES } from 'src/common/constants';
import moment from 'moment';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  filterAllowedStatus,
  FilteredStatus,
} from '../ServiceRequests/helpers/allowedStatus';
import { displayStatusLabel } from 'src/utils/serviceRequestStatus/displayStatusLabel';
import { useStatusLabelResolver } from 'src/utils/serviceRequestStatus/useStatusLabelResolver';
import './service-request-toast.scss';

interface PendingStatusCopy {
  title: string;
  message: string;
  confirmLabel: string;
}

interface StatusOption extends ModernSelectOption {}

const buildPendingStatusCopy = (
  status: string,
  displayLabel: string
): PendingStatusCopy => {
  if (status === 'Archive') {
    return {
      title: 'Archive service request?',
      message: 'Are you sure you want to archive this service request?',
      confirmLabel: 'Archive',
    };
  }

  return {
    title: `Change status to ${displayLabel}?`,
    message: `Are you sure you want to change the status to ${displayLabel.toLowerCase()}?`,
    confirmLabel: 'Change status',
  };
};

const ServiceRequestToast = ({ serviceRequest, handleStatusChange }) => {
  const { branding } = useBranding();

  const resolveStatusLabel = useStatusLabelResolver();

  let statuses: any = STATUSES;

  let allowedStatuses: FilteredStatus[] = useMemo(() => {
    return filterAllowedStatus(statuses, branding);
  }, [branding]);

  let activeStatus =
    serviceRequest.status && serviceRequest.status.name
      ? serviceRequest.status.name
      : '';

  const currentStatusAllowed: boolean = useMemo(() => {
    return allowedStatuses.some(
      (s: FilteredStatus) => s.label === activeStatus
    );
  }, [allowedStatuses, activeStatus]);

  const statusOptions: StatusOption[] = useMemo(() => {
    const allowed: StatusOption[] = allowedStatuses.map(
      (stat: FilteredStatus) => ({
        value: stat.value,
        label: resolveStatusLabel(stat.value),
      })
    );

    if (activeStatus && !currentStatusAllowed) {
      return [
        {
          value: activeStatus,
          label: displayStatusLabel(
            activeStatus,
            serviceRequest.status?.customLabel
          ),
        },
        ...allowed,
      ];
    }

    return allowed;
  }, [
    allowedStatuses,
    activeStatus,
    currentStatusAllowed,
    resolveStatusLabel,
    serviceRequest.status?.customLabel,
  ]);

  const selectedOption: StatusOption | null = activeStatus
    ? {
        value: activeStatus,
        label: displayStatusLabel(
          activeStatus,
          serviceRequest.status?.customLabel
        ),
      }
    : null;

  let statusMessage = STATUS_MESSAGES(branding).filter(
    (sm) => sm.status === activeStatus
  )[0]?.message;

  if (statusMessage) {
    if (activeStatus === 'En Route' || activeStatus === 'In Progress') {
      statusMessage =
        statusMessage +
        `${serviceRequest.suite.property.name} - #${serviceRequest.suite.number || serviceRequest.commonArea.name}.`;
    } else if (activeStatus === 'Scheduled') {
      statusMessage =
        statusMessage +
        `${moment(serviceRequest.appointment.scheduledAt).format('dddd MMMM DD, YYYY hh:mmA')}.`;
    }
  } else {
    statusMessage = '';
  }

  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const pendingStatusCopy = pendingStatus
    ? buildPendingStatusCopy(pendingStatus, resolveStatusLabel(pendingStatus))
    : null;

  const handleConfirmStatusChange = () => {
    if (!pendingStatus) {
      return;
    }

    handleStatusChange({ name: pendingStatus }, serviceRequest.id);
    setPendingStatus(null);
  };

  const handleCancelStatusChange = () => {
    setPendingStatus(null);
  };

  return (
    <>
      <Toast
        color={activeStatus === 'On Hold' ? 'yellow' : 'teal'}
        message={statusMessage}
        actions={
          <div className="service-request-toast-actions">
            {activeStatus !== 'Declined' &&
              branding.permissions.serviceRequestStatus.current?.declined && (
                <Button
                  size="tiny"
                  label="Decline"
                  outline="teal"
                  onClick={() => setPendingStatus('Declined')}
                />
              )}
            <ModernSelect<StatusOption>
              options={statusOptions}
              value={selectedOption}
              isSearchable={false}
              isOptionDisabled={(option: any) => option.value === activeStatus}
              onChange={(option: any) => {
                if (option && option.value !== activeStatus) {
                  setPendingStatus(option.value);
                }
              }}
            />
          </div>
        }
      />
      {pendingStatusCopy && (
        <ConfirmationModal
          title={pendingStatusCopy.title}
          message={pendingStatusCopy.message}
          confirmLabel={pendingStatusCopy.confirmLabel}
          onConfirm={handleConfirmStatusChange}
          onCancel={handleCancelStatusChange}
          variant="warning"
        />
      )}
    </>
  );
};

export default ServiceRequestToast;
